import { useDispatch, useSelector } from 'react-redux';

import { RootState } from 'src/store/reducers';

import SuccessIconNoBackground from '@images/svg-icons/successIconNoBackground.svg';
import PreLoader from '@images/svg-icons/preLoader.svg';

import {
    PersonalDetails,
    PrimaryFormFields,
    SecondaryFormFields,
    ThirdFormFields,
    UpdatePersonalDetailsPayload,
} from '@interfaces/myProfileInterfaces';

import PersonalDetailsPrimaryForm from './PersonalDetailsPrimaryForm';
import PersonalDetailsSecondaryForm from './PersonalDetailsSecondaryForm';
import PersonalDetailsThirdForm from './PersonalDetailsThirdForm';

import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';

import { updatePersonalDetails } from '@services/myProfileServices';

import { getLabelFallback, getPartialPhoneNumber } from '@utils/utils';
import { replaceLabelPlaceholder } from '@utils/pages-utils/EyeExamsUtils';
import { isOfTypeGender, isValidZipCode } from '@utils/pages-utils/MyProfileUtils';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { isValid, differenceInYears, compareAsc } from 'date-fns';
import { getStringFromDate } from '@utils/dateUtils';

interface Props {
    personalDetails: PersonalDetails;
}

const PersonalDetailsForm = ({ personalDetails }: Props) => {
    const config = useSelector((state: RootState) => state.common.config);
    const updatePersonalDetailsRequestStatus = useSelector(
        (state: RootState) => state.myProfile.updatePersonalDetailsRequestStatus
    );

    const dispatch = useDispatch();

    const schema = yup.object().shape({
        billingFirstName: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGFIRSTNAME_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_BILLINGFIRSTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        billingLastName: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGLASTNAME_LABEL')
                )
            )
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_BILLINGLASTNAME'),
                val => (val ? !/\d/.test(val) : true)
            ),
        billingAddressLine1: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGADDRESSLINE1_LABEL')
                )
            ),
        billingAddressLine2: yup.string(),
        billingZipcode: yup
            .string()
            .test(
                'len',
                getLabelFallback(
                    config.countryName.toUpperCase() === 'CA'
                        ? 'MYPROFILE_PERSONAL_DETAILS_INVALID_BILLING_POSTAL_CODE_MESSAGE'
                        : 'MYPROFILE_PERSONAL_DETAILS_INVALID_BILLING_ZIP_CODE_MESSAGE'
                ),
                val => (val ? isValidZipCode(val.toString(), config) : true)
            )
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback(
                        config.countryName === 'CA'
                            ? 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_CA_LABEL'
                            : 'MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGZIPCODE_OTHER_COUNTRIES_LABEL'
                    )?.toLowerCase()
                )
            ),
        billingCity: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_BILLINGCITY_LABEL')
                )
            ),
        billingState: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback(
                        config.countryName === 'CA'
                            ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_PROVINCES_LABEL'
                            : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_BILLING_STATE_LABEL'
                    )
                )
            ),
        shipToBillingAddress: yup.boolean(),
        shippingFirstName: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGFIRSTNAME_LABEL')
                    )
                )
                .test(
                    'invalid',
                    getLabelFallback(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_SHIPPINGFIRSTNAME'
                    ),
                    val => (val ? !/\d/.test(val) : true)
                ),
        }),
        shippingLastName: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGLASTNAME_LABEL')
                    )
                )
                .test(
                    'invalid',
                    getLabelFallback(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_INVALID_SHIPPINGLASTNAME'
                    ),
                    val => (val ? !/\d/.test(val) : true)
                ),
        }),
        shippingAddressLine1: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback(
                            'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGADDRESSLINE1_LABEL'
                        )
                    )
                ),
        }),

        shippingAddressLine2: yup.string(),
        shippingZipcode: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .test(
                    'len',
                    getLabelFallback(
                        config.countryName === 'CA'
                            ? 'MYPROFILE_PERSONAL_DETAILS_INVALID_SHIPPING_POSTAL_CODE_MESSAGE'
                            : 'MYPROFILE_PERSONAL_DETAILS_INVALID_SHIPPING_ZIP_CODE_MESSAGE'
                    ),
                    val => (val ? isValidZipCode(val.toString(), config) : true)
                )
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback(
                            config.countryName === 'CA'
                                ? 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGPOSTALCODE_CA_LABEL'
                                : 'MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGZIPCODE_OTHER_COUNTRIES_LABEL'
                        )?.toLowerCase()
                    )
                ),
        }),

        shippingCity: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_SHIPPINGCITY_LABEL')
                    )
                ),
        }),

        shippingState: yup.string().when('shipToBillingAddress', {
            is: false,
            then: yup
                .string()
                .required(
                    replaceLabelPlaceholder(
                        'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                        '{FORM_FIELD}',
                        getLabelFallback(
                            config.countryName === 'CA'
                                ? 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_PROVINCES_LABEL'
                                : 'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SHIPPING_STATE_LABEL'
                        )
                    )
                ),
        }),
        email: yup
            .string()
            .email(getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_EMAIL_MESSAGE'))
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_EMAIL_LABEL')
                )
            ),
        isDefault: yup.boolean(),
        phoneNumber1: yup
            .string()
            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_PHONENUMBER1_LABEL')
                )
            )
            .test(
                'len',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_PHONENUMBER1'),
                val => (val ? val.replace(/\D/g, '').length >= 10 : true)
            ),

        phoneNumber2: yup
            .string()
            .test(
                'len',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_FORM_INVALID_PHONENUMBER2'),
                val => (val ? val.replace(/\D/g, '').length >= 10 : true)
            ),
        dob: yup
            .string()
            .test(
                'invalid',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'),
                value => {
                    return isValid(new Date(value)) &&
                        compareAsc(new Date(value), new Date('01/01/1900')) !== -1 &&
                        compareAsc(new Date(value), new Date()) !== 1 &&
                        value.replace(/\//g, '').length === 8
                        ? true
                        : false;
                }
            )
            .test(
                'min',
                getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_OF_BIRTH_MESSAGE'),
                value => !(differenceInYears(new Date(), new Date(value)) < 18)
            )

            .required(
                replaceLabelPlaceholder(
                    'MYPROFILE_PERSONAL_DETAILS_FORM_FIELD_CANNOT_BE_EMPTY_MESSAGE',
                    '{FORM_FIELD}',
                    getLabelFallback('MYPROFILE_PERSONAL_DETAILS_THIRD_FORM_BIRTHDATE_LABEL')
                )
            )
            .typeError(getLabelFallback('MYPROFILE_PERSONAL_DETAILS_INVALID_DATE_MESSAGE'))

            .nullable(),
        gender: yup.string(),
    });

    const defaultFormValues: PrimaryFormFields & SecondaryFormFields & ThirdFormFields = {
        billingFirstName: personalDetails?.billingAddress?.firstName ?? '',
        billingLastName: personalDetails?.billingAddress?.lastName ?? '',
        billingAddressLine1: personalDetails?.billingAddress?.addressLine1 ?? '',
        billingAddressLine2: personalDetails?.billingAddress?.addressLine2 ?? '',
        billingZipcode: personalDetails?.billingAddress?.zipcode ?? '',
        billingCity: personalDetails?.billingAddress?.city ?? '',
        billingState: personalDetails?.billingAddress?.state ?? '',
        shipToBillingAddress:
            personalDetails?.oldAddressType === 'SB'
                ? true
                : !personalDetails?.oldAddressType
                ? true
                : false,
        shippingFirstName: personalDetails?.shippingAddress?.firstName ?? '',
        shippingLastName: personalDetails?.shippingAddress?.lastName ?? '',
        shippingAddressLine1: personalDetails?.shippingAddress?.addressLine1 ?? '',
        shippingAddressLine2: personalDetails?.shippingAddress?.addressLine2 ?? '',
        shippingZipcode: personalDetails?.shippingAddress?.zipcode ?? '',
        shippingCity: personalDetails?.shippingAddress?.city ?? '',
        shippingState: personalDetails?.shippingAddress?.state ?? '',
        email: personalDetails?.email ?? '',
        phoneNumber1: personalDetails?.phone1?.phoneNumber ?? '',
        //phoneNumber2: personalDetails?.phone2?.phoneNumber ?? '',
        gender: isOfTypeGender(personalDetails?.gender) ? personalDetails?.gender : '',
        dob: personalDetails?.dob
            ? getStringFromDate(new Date(personalDetails.dob), 'MM/dd/yyyy')
            : '',
    };

    const methods = useForm<PrimaryFormFields | SecondaryFormFields | ThirdFormFields>({
        resolver: yupResolver(schema),
        defaultValues: defaultFormValues,
    });

    const onSubmit: SubmitHandler<PrimaryFormFields & SecondaryFormFields & ThirdFormFields> = (
        data: PrimaryFormFields & SecondaryFormFields & ThirdFormFields
    ) => {
        const updatePersonalDetailsPayload: UpdatePersonalDetailsPayload = {
            isMockRequest: config.mockRequest,
            storeId: +config.storeId,
            catalogId: +config.catalogId,
            langId: config.langId,
            logonId: `${config.storeId}|${config.userEmail}`,
            memberId: String(config.userId),
            oldAddressType:
                !personalDetails?.addressId &&
                !personalDetails?.shipaddressId &&
                !personalDetails?.oldAddressType
                    ? 'SB'
                    : personalDetails?.oldAddressType,
            disableExtendedChecking: true,
            emailOptintype: 2,
            primary: 1,
            ...(personalDetails?.addressId && { addressId: personalDetails?.addressId }),
            ...(personalDetails?.shipaddressId && {
                shipaddressId: personalDetails?.shipaddressId,
            }),
            ...(!personalDetails?.addressId &&
                !personalDetails?.shipaddressId &&
                !personalDetails?.oldAddressType && {
                    shipNickName: 'DefaultNickShip',
                    nickName: 'DefaultNick',
                    nickNameDefault: 'nickNameDefault',
                }),
            ...(personalDetails?.addressId &&
                !personalDetails?.shipaddressId &&
                personalDetails?.oldAddressType === 'SB' && {
                    shipNickName: 'DefaultNickShip',
                }),
            firstName: data.billingFirstName,
            lastName: data.billingLastName,
            address1: data.billingAddressLine1,
            address2: data.billingAddressLine2 ?? null,
            city: data.billingCity,
            state: data.billingState,
            zipCode: data.billingZipcode,
            SameShippingAndBillingAddressCustom: data.shipToBillingAddress
                ? 'sameAsBilling'
                : 'diffAsBilling',
            shipping_firstName: data.shippingFirstName ?? null,
            shipping_lastName: data.shippingLastName ?? null,
            shipping_address1: data.shippingAddressLine1 ?? null,
            shipping_address2: data.shippingAddressLine2 ?? null,
            shipping_city: data.shippingCity ?? null,
            shipping_state: data.shippingState ?? null,
            shipping_zipCode: data.shippingZipcode ?? null,
            country: config.countryName,
            URL: 'PersonalInformationView',
            updateContactInfo: true,
            errorViewName: 'PersonalInformationView',
            email1: data.email,
            phone1Type: personalDetails?.phone1?.phoneType ?? 'CEL',
            phone1_1: getPartialPhoneNumber(data.phoneNumber1, 0, 3),
            phone1_2: getPartialPhoneNumber(data.phoneNumber1, 3, 6),
            phone1_3: getPartialPhoneNumber(data.phoneNumber1, 6, 10),
            phone1: data.phoneNumber1.replace(/\D/g, ''),
            phone2Type: personalDetails?.phone2?.phoneType ?? 'CEL',
            phone2_1: null, //data.phoneNumber2 ? getPartialPhoneNumber(data.phoneNumber2, 0, 3) : null,
            phone2_2: null, //data.phoneNumber2 ? getPartialPhoneNumber(data.phoneNumber2, 3, 6) : null,
            phone2_3: null, //data.phoneNumber2 ? getPartialPhoneNumber(data.phoneNumber2, 6, 10) : null,
            phone2: null, //data.phoneNumber2 ? data.phoneNumber2.replace(/\D/g, '') : null,
            dateOfBirthInfo: true,
            birth_month: String(new Date(data.dob).getMonth() + 1).padStart(2, '0'),
            birth_date: String(new Date(data.dob).getDate()).padStart(2, '0'),
            birth_year: String(new Date(data.dob).getFullYear()),
            dateOfBirth: getStringFromDate(new Date(data.dob), 'yyyy-MM-dd'),
            gender: data.gender,
        };
        //dispatch(updatePersonalDetails(updatePersonalDetailsPayload));
    };

    return (
        <div className="personal-details-forms-container content-container">
            <FormProvider {...methods}>
                <form autoComplete="on" onSubmit={methods.handleSubmit(onSubmit)}>
                    <PersonalDetailsPrimaryForm defaultFormValues={defaultFormValues} />
                    <PersonalDetailsSecondaryForm />
                    <PersonalDetailsThirdForm />
                    <div className="form-footer">
                        <button
                            data-element-id="PersonalDetails_Save"
                            className={`primary-button button-size-medium ${updatePersonalDetailsRequestStatus === 'LOADING' ? 'loading' : ''
                                }`}
                            type="submit"
                        >
                            {getLabelFallback(
                                'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SAVE_CHANGES_CTA'
                            )}
                            {updatePersonalDetailsRequestStatus === 'LOADING' && <PreLoader />}
                        </button>

                        {updatePersonalDetailsRequestStatus === 'SUCCESS' && (
                            <div className="success-message">
                                <SuccessIconNoBackground />
                                <span className="secondary-message medium success bold">
                                    {getLabelFallback(
                                        'MYPROFILE_PERSONAL_DETAILS_PRIMARY_FORM_SUCCESS_MESSAGE'
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

export default PersonalDetailsForm;
